export const DETECTOR_VERSION = "mediapipe-pose-v2";

export const DETECTOR_LIMITS = Object.freeze({
  addressHoldMs: 900,
  recordingTimeoutMs: 12000,
  followThroughHoldMs: 900,
  cooldownMs: 1800,
  trackingGraceMs: 450,
  addressBreakGraceMs: 250,
  motionStartThreshold: 1.2,
  motionStopThreshold: 0.48,
  minimumVisibility: 0.45,
});

export const REQUIRED_POSE_LANDMARKS = Object.freeze([
  { index: 11, name: "left shoulder" },
  { index: 12, name: "right shoulder" },
  { index: 15, name: "left wrist" },
  { index: 16, name: "right wrist" },
  { index: 23, name: "left hip" },
  { index: 24, name: "right hip" },
  { index: 27, name: "left ankle" },
  { index: 28, name: "right ankle" },
]);

const midpoint = (left, right) => ({
  x: (left.x + right.x) / 2,
  y: (left.y + right.y) / 2,
});

const distance = (left, right) => Math.hypot(left.x - right.x, left.y - right.y);

const visible = (landmark) => (
  landmark && (landmark.visibility ?? landmark.presence ?? 1) >= DETECTOR_LIMITS.minimumVisibility
);

const midpointWithOcclusionFallback = (left, right) => {
  const leftVisible = visible(left);
  const rightVisible = visible(right);
  if (leftVisible && rightVisible) return midpoint(left, right);
  if (leftVisible) return { x: left.x, y: left.y };
  if (rightVisible) return { x: right.x, y: right.y };
  return null;
};

export const poseSampleFromLandmarks = (poses, previousSample, timestamp) => {
  if (!Array.isArray(poses) || poses.length !== 1) {
    return {
      timestamp,
      valid: false,
      poseCount: poses?.length || 0,
      motion: 0,
      address: false,
      missingJoints: [],
      trackingQuality: "blocked",
    };
  }

  const landmarks = poses[0];
  const missingJoints = REQUIRED_POSE_LANDMARKS
    .filter(({ index }) => !visible(landmarks[index]))
    .map(({ name }) => name);
  const shouldersVisible = visible(landmarks[11]) && visible(landmarks[12]);
  const hipsVisible = visible(landmarks[23]) && visible(landmarks[24]);
  const wristsVisible = visible(landmarks[15]) || visible(landmarks[16]);
  const anklesVisible = visible(landmarks[27]) || visible(landmarks[28]);
  if (!shouldersVisible || !hipsVisible || !wristsVisible || !anklesVisible) {
    return {
      timestamp,
      valid: false,
      poseCount: 1,
      motion: 0,
      address: false,
      missingJoints,
      trackingQuality: "blocked",
    };
  }

  const shoulders = midpoint(landmarks[11], landmarks[12]);
  const hips = midpoint(landmarks[23], landmarks[24]);
  const wrists = midpointWithOcclusionFallback(landmarks[15], landmarks[16]);
  const ankles = midpointWithOcclusionFallback(landmarks[27], landmarks[28]);
  const torso = Math.max(distance(shoulders, hips), 0.05);
  const address = wrists.y > shoulders.y && wrists.y < ankles.y && hips.y < ankles.y;

  let motion = 0;
  const elapsedSincePrevious = timestamp - (previousSample?.timestamp ?? timestamp);
  if (
    previousSample?.valid
    && elapsedSincePrevious > 0
    && elapsedSincePrevious <= DETECTOR_LIMITS.trackingGraceMs
  ) {
    const elapsedSeconds = Math.max(elapsedSincePrevious / 1000, 0.025);
    const wristVelocity = distance(wrists, previousSample.wrists) / torso / elapsedSeconds;
    const shoulderVelocity = distance(shoulders, previousSample.shoulders) / torso / elapsedSeconds;
    const hipVelocity = distance(hips, previousSample.hips) / torso / elapsedSeconds;
    motion = wristVelocity * 0.65 + shoulderVelocity * 0.2 + hipVelocity * 0.15;
  }

  return {
    timestamp,
    valid: true,
    poseCount: 1,
    address,
    motion,
    missingJoints,
    trackingQuality: missingJoints.length > 0 ? "partial" : "full",
    wrists,
    shoulders,
    hips,
  };
};

export const initialDetectorState = () => ({
  phase: "positioning",
  stableSince: null,
  recordingStartedAt: null,
  quietSince: null,
  swingSeen: false,
  cooldownUntil: null,
  trackingLostSince: null,
});

export const advanceDetector = (state, sample, timestamp) => {
  const next = { ...state };
  const actions = [];

  if (state.phase === "cooldown") {
    if (timestamp >= state.cooldownUntil) return { state: initialDetectorState(), actions };
    return { state: next, actions };
  }

  if (state.phase === "positioning") {
    if (sample.valid && sample.address && sample.motion <= DETECTOR_LIMITS.motionStopThreshold) {
      next.stableSince = state.stableSince ?? timestamp;
      next.phase = "address_stable";
    } else {
      next.phase = "positioning";
      next.stableSince = null;
    }
    return { state: next, actions };
  }

  if (state.phase === "address_stable") {
    if (sample.valid && sample.address && sample.motion <= DETECTOR_LIMITS.motionStopThreshold) {
      next.trackingLostSince = null;
      if (timestamp - state.stableSince >= DETECTOR_LIMITS.addressHoldMs) {
        next.phase = "recording";
        next.recordingStartedAt = timestamp;
        next.quietSince = null;
        actions.push("start_recording");
      }
    } else {
      next.trackingLostSince = state.trackingLostSince ?? timestamp;
      const graceMs = sample.valid
        ? DETECTOR_LIMITS.addressBreakGraceMs
        : DETECTOR_LIMITS.trackingGraceMs;
      if (timestamp - next.trackingLostSince > graceMs) {
        next.phase = "positioning";
        next.stableSince = null;
        next.trackingLostSince = null;
      }
    }
    return { state: next, actions };
  }

  if (state.phase === "recording") {
    if (sample.valid && sample.motion >= DETECTOR_LIMITS.motionStartThreshold) {
      next.phase = "swing_active";
      next.swingSeen = true;
      next.quietSince = null;
      actions.push("swing_started");
    } else if (timestamp - state.recordingStartedAt >= DETECTOR_LIMITS.recordingTimeoutMs) {
      next.phase = "positioning";
      next.recordingStartedAt = null;
      next.stableSince = null;
      actions.push("abort_recording");
    }
    return { state: next, actions };
  }

  if (state.phase === "swing_active") {
    if (sample.valid && sample.motion <= DETECTOR_LIMITS.motionStopThreshold) {
      next.quietSince = state.quietSince ?? timestamp;
      next.phase = "follow_through";
    }
    if (timestamp - state.recordingStartedAt >= DETECTOR_LIMITS.recordingTimeoutMs) {
      next.phase = "validating";
      actions.push("stop_recording");
    }
    return { state: next, actions };
  }

  if (state.phase === "follow_through") {
    if (!sample.valid) {
      next.trackingLostSince = state.trackingLostSince ?? timestamp;
      if (timestamp - next.trackingLostSince > DETECTOR_LIMITS.trackingGraceMs) {
        next.phase = "swing_active";
        next.quietSince = null;
        next.trackingLostSince = null;
      }
    } else if (sample.motion > DETECTOR_LIMITS.motionStopThreshold) {
      next.phase = "swing_active";
      next.quietSince = null;
      next.trackingLostSince = null;
    } else {
      next.trackingLostSince = null;
      if (timestamp - state.quietSince >= DETECTOR_LIMITS.followThroughHoldMs) {
        next.phase = "validating";
        actions.push("stop_recording");
      }
    }
    if (timestamp - state.recordingStartedAt >= DETECTOR_LIMITS.recordingTimeoutMs) {
      next.phase = "validating";
      actions.push("stop_recording");
    }
  }
  return { state: next, actions: [...new Set(actions)] };
};

export const beginManualCapture = (timestamp) => ({
  ...initialDetectorState(),
  phase: "recording",
  recordingStartedAt: timestamp,
});

export const beginCooldown = (timestamp) => ({
  ...initialDetectorState(),
  phase: "cooldown",
  cooldownUntil: timestamp + DETECTOR_LIMITS.cooldownMs,
});
